import { AppConfig } from "./AppConfig.js";

export class ApiCache {
  static cachePrefix = "api_cache_v1:";
  static memoryCache = new Map();
  static inFlight = new Map();

  // "permanente" = datos cuya frescura la decide el sello del back (ver EstadoService), no el reloj.
  // 100 años, no Infinity: JSON.stringify(Infinity) da null y read() descartaria la entrada.
  static TTL_PERMANENTE = 100 * 365 * 86400000;

  // "500ms" | "30s" | "5m" | "1h" | "2d" | "permanente" | número en ms → ms
  static parseTTL(ttl) {
    if (!ttl) return 0;
    if (typeof ttl === "number") return ttl;
    if (typeof ttl === "string") {
      if (ttl.trim().toLowerCase() === "permanente") return this.TTL_PERMANENTE;
      const m = ttl.trim().match(/^(\d+)\s*(ms|s|m|h|d)$/i);
      if (!m) throw new Error("TTL invalido: " + ttl);
      const value = parseInt(m[1], 10);
      const multipliers = { ms: 1, s: 1000, m: 60000, h: 3600000, d: 86400000 };
      return value * multipliers[m[2].toLowerCase()];
    }
    return 0;
  }

  static buildKey({ method, url, data, token }) {
    const tokenKey = token ? "t:" + token : "t:public";
    let bodyKey = "";
    if (data) bodyKey = data instanceof FormData ? "formdata" : JSON.stringify(data);
    return method + "|" + url + "|" + tokenKey + "|" + bodyKey;
  }

  static getStorageKey(cacheKey) {
    return this.cachePrefix + cacheKey;
  }

  static read(cacheKey) {
    const now = Date.now();
    const mem = this.memoryCache.get(cacheKey);
    if (mem && mem.expiresAt > now) return mem.data;
    try {
      const raw = localStorage.getItem(this.getStorageKey(cacheKey));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.expiresAt || parsed.expiresAt <= now) {
        localStorage.removeItem(this.getStorageKey(cacheKey));
        return null;
      }
      this.memoryCache.set(cacheKey, { expiresAt: parsed.expiresAt, data: parsed.data });
      return parsed.data;
    } catch {
      localStorage.removeItem(this.getStorageKey(cacheKey));
      return null;
    }
  }

  static write(cacheKey, data, ttlMs, persistent = true) {
    const expiresAt = Date.now() + ttlMs;
    this.memoryCache.set(cacheKey, { expiresAt, data });
    if (persistent) {
      try {
        localStorage.setItem(this.getStorageKey(cacheKey), JSON.stringify({ expiresAt, data }));
      } catch (e) {
        if (AppConfig.features?.debug) console.warn("No se pudo guardar cache:", e);
      }
    }
  }

  // Invalida memoria + localStorage. Ej: tras un POST que muta pólizas →
  // ApiService.invalidate("polizas")
  static invalidate({ urlContains = "", token = null } = {}) {
    const tokenKey = token ? "t:" + token : null;
    for (const key of this.memoryCache.keys()) {
      if (key.includes(urlContains) && (!tokenKey || key.includes(tokenKey))) {
        this.memoryCache.delete(key);
      }
    }
    try {
      const keysToDelete = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith(this.cachePrefix)) continue;
        const cacheKey = k.replace(this.cachePrefix, "");
        if (cacheKey.includes(urlContains) && (!tokenKey || cacheKey.includes(tokenKey))) {
          keysToDelete.push(k);
        }
      }
      keysToDelete.forEach((k) => localStorage.removeItem(k));
    } catch {}
  }

  static getInFlight(cacheKey) { return this.inFlight.get(cacheKey) || null; }
  static setInFlight(cacheKey, promise) { this.inFlight.set(cacheKey, promise); }
  static clearInFlight(cacheKey) { this.inFlight.delete(cacheKey); }

  static canCache({ ttlMs, data }) {
    if (!ttlMs || ttlMs <= 0) return false;
    if (data instanceof FormData) return false;
    return true;
  }
}
